import { useAuth } from '@/contexts/AuthContext'
import { useRouteAccess } from '@/hooks/useRouteAccess'
import { api } from '@/lib/api'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

/**
 * Protege a rota por autenticação e por permissão de menu.
 * Se o usuário não tiver permissão para a rota atual (menu não liberado para o perfil),
 * redireciona para o primeiro menu liberado para o usuário (por ordem).
 */
type TipoBloqueioInadimplencia = 'GLOBAL' | 'TELA' | null

export function ProtectedByPermissionRoute({
  children
}: {
  children: JSX.Element
}) {
  const { user, loading: authLoading, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const {
    canAccessPath,
    firstAllowedRoute,
    loading: routeAccessLoading
  } = useRouteAccess()
  const [paymentLoading, setPaymentLoading] = useState(true)
  const [paymentBlockedMessage, setPaymentBlockedMessage] = useState<
    string | null
  >(null)
  const [paymentBlockedMotivo, setPaymentBlockedMotivo] = useState<
    string | null
  >(null)
  const [paymentBlockedTipo, setPaymentBlockedTipo] =
    useState<TipoBloqueioInadimplencia>(null)
  const [avisoFechado, setAvisoFechado] = useState(false)
  const [paymentWarning, setPaymentWarning] = useState<{
    mensagem?: string | null
    observacao?: string | null
    data_vencimento?: string | null
    dias_restantes?: number | null
  } | null>(null)
  const pathname = location.pathname || '/'
  const hasRouteAccess = canAccessPath(pathname)

  useEffect(() => {
    let cancelled = false

    const verifyPaymentAccess = async () => {
      if (authLoading || !user || routeAccessLoading) {
        if (!cancelled) setPaymentLoading(true)
        return
      }

      if (!hasRouteAccess) {
        if (!cancelled) {
          setPaymentBlockedMessage(null)
          setPaymentBlockedMotivo(null)
          setPaymentBlockedTipo(null)
          setPaymentWarning(null)
          setPaymentLoading(false)
        }
        return
      }

      try {
        setPaymentLoading(true)
        const response = await api.get(
          `/api/inadimplencias/verificar-acesso?rota_atual=${encodeURIComponent(pathname)}`
        )
        const bloqueio = response?.result

        if (!cancelled) {
          if (bloqueio?.bloqueado) {
            setPaymentBlockedMessage(bloqueio?.mensagem || null)
            setPaymentBlockedMotivo(bloqueio?.motivo_bloqueio || null)
            const reg = bloqueio?.registro as { id_tela?: number | null } | null
            let tipo: TipoBloqueioInadimplencia =
              (bloqueio?.tipo as TipoBloqueioInadimplencia) ?? null
            if (tipo !== 'GLOBAL' && tipo !== 'TELA') {
              tipo = reg?.id_tela == null ? 'GLOBAL' : 'TELA'
            }
            setPaymentBlockedTipo(tipo)
            setPaymentWarning(null)
          } else {
            setPaymentBlockedMessage(null)
            setPaymentBlockedMotivo(null)
            setPaymentBlockedTipo(null)
            const aviso = bloqueio?.aviso
            if (aviso?.ativo && aviso?.mensagem) {
              setPaymentWarning({
                mensagem: aviso.mensagem,
                observacao: aviso.observacao ?? null,
                data_vencimento: aviso.data_vencimento ?? null,
                dias_restantes: aviso.dias_restantes ?? null
              })
            } else {
              setPaymentWarning(null)
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setPaymentBlockedMessage(null)
          setPaymentBlockedMotivo(null)
          setPaymentBlockedTipo(null)
          setPaymentWarning(null)
        }
      } finally {
        if (!cancelled) {
          setPaymentLoading(false)
        }
      }
    }

    verifyPaymentAccess()

    return () => {
      cancelled = true
    }
  }, [authLoading, user, routeAccessLoading, hasRouteAccess, pathname])

  useEffect(() => {
    setAvisoFechado(false)
  }, [pathname, paymentWarning?.mensagem])

  const isBloqueioGlobal = paymentBlockedTipo === 'GLOBAL'

  const irParaLoginAposLogout = async () => {
    await signOut()
    const loginHref = new URL(
      'login',
      `${window.location.origin}${import.meta.env.BASE_URL}`
    ).href
    window.location.replace(loginHref)
  }

  if (authLoading) {
    return <div style={{ padding: 24 }}>Carregando...</div>
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Enquanto permissões/menus carregam, evita redirecionar em falso negativo
  if (routeAccessLoading) {
    return <div style={{ padding: 24 }}>Carregando...</div>
  }

  if (paymentLoading) {
    return <div style={{ padding: 24 }}>Carregando...</div>
  }

  if (paymentBlockedMessage) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#1f2937',
          boxSizing: 'border-box'
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: '100%',
            background: '#fff',
            border: '1px solid #fecaca',
            color: '#991b1b',
            borderRadius: 16,
            padding: '28px 24px 24px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)'
          }}
        >
          <h1 style={{ margin: 0, fontSize: 22, lineHeight: 1.2 }}>
            Acesso bloqueado
          </h1>
          <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.5 }}>
            {paymentBlockedMessage}
          </p>
          {paymentBlockedMotivo ? (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: '#fef2f2',
                borderRadius: 8,
                border: '1px solid #fecaca',
                fontSize: 14,
                lineHeight: 1.5,
                color: '#7f1d1d'
              }}
            >
              <strong style={{ display: 'block', marginBottom: 6 }}>
                Motivo do bloqueio
              </strong>
              {paymentBlockedMotivo}
            </div>
          ) : null}
          <div style={{ marginTop: 24 }}>
            <button
              type="button"
              onClick={() =>
                isBloqueioGlobal
                  ? void irParaLoginAposLogout()
                  : navigate('/dashboard', { replace: true })
              }
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                fontWeight: 600,
                color: '#fff',
                background: '#2563eb',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer'
              }}
            >
              {isBloqueioGlobal
                ? 'Sair e ir para o login'
                : 'Voltar para o dashboard'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!hasRouteAccess) {
    return <Navigate to={firstAllowedRoute} replace />
  }

  const mostrarAvisoFlutuante =
    Boolean(paymentWarning?.mensagem) && !avisoFechado

  return (
    <>
      {mostrarAvisoFlutuante ? (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            left: 24,
            maxWidth: 420,
            marginLeft: 'auto',
            zIndex: 10000,
            padding: '16px 44px 16px 16px',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 12,
            color: '#92400e',
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: 'pre-line',
            boxShadow: '0 10px 40px rgba(0,0,0,0.18)'
          }}
        >
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={() => setAvisoFechado(true)}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 32,
              height: 32,
              padding: 0,
              lineHeight: '28px',
              fontSize: 22,
              color: '#78350f',
              background: 'transparent',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            ×
          </button>
          <strong style={{ display: 'block', marginBottom: 6 }}>
            Aviso — pendência financeira
          </strong>
          {paymentWarning?.mensagem}
        </div>
      ) : null}
      {children}
    </>
  )
}
